import { Injectable, Output, EventEmitter } from "@angular/core";
import { MatDialog } from "@angular/material";

import {
	isResourcePortSingle,
	isResourcePortRange,
	Port,
	ResourceCreateRequest,
	ResourceUpdateRequest,
	Resource
} from "@impulse/sdp-shared-models";

import {
	ApplicationWizardState,
	ApplicationWizardNetworkService,
	ApplicationWizardPortsProtocols,
	ApplicationWizardSelectType
} from "./models";
import { Application } from "../../../models";
import { ApiGatewayService } from "../../../common/api-gateway/api-gateway.service";

@Injectable()
export class ApplicationWizardService {
	public applicationState = this.newApplicationState();

	@Output() public submitSuccessEvent: EventEmitter<string> = new EventEmitter(true);

	constructor(
		public dialog: MatDialog,
		private apiGatewayService: ApiGatewayService
	) { }

	public newApplicationState(): ApplicationWizardState {
		// TODO we need a better way to track application state than this
		return {
			application: {
				name: "",
				urlName: "",
				version: 6,
				ip: "",
				ports: [
					{
						version: 2,
						port: 0,
						portNumber: 0,
						proto: "tcp"
					}
				],
				icmp: true,
				urlPrefix: undefined,
				urlSuffix: undefined,
				createdAt: 0,
				updatedAt: 0,
				image: "",
				selectType: "website",
				access: {
					samlAliases: [],
					users: []
				}
			},
			webUrl: "",
			isCreate: true
		};
	}

	public getValue(key: string, defaultValue: any) {
		const data = <any>(key === "webUrl" ? this.applicationState : this.applicationState.application) || {};
		return data[key] !== undefined ? data[key] : defaultValue;
	}

	public async processApplication() {
		const application = this.applicationState.application;

		if (this.applicationState.isCreate) {
			// TODO Can I store something more like this request directly instead of translating here?
			const request: ResourceCreateRequest = {
				name: application.name,
				ip: application.ip,
				ports: application.ports,
				selectType: application.selectType,
				icmp: application.icmp,
				image: application.image,
				urlPrefix: application.urlPrefix,
				urlSuffix: application.urlSuffix,
				access: {
					samlAliases: [],
					users: []
				}
			};
			await this.apiGatewayService.createApplication(request);
		} else {
			// TODO same as create, can we streamline this?
			const request: ResourceUpdateRequest = {
				name: application.name,
				ip: application.ip,
				ports: application.ports,
				selectType: application.selectType,
				icmp: application.icmp,
				image: application.image,
				urlPrefix: application.urlPrefix,
				urlSuffix: application.urlSuffix
			};
			await this.apiGatewayService.updateApplication(request, application.urlName);
		}
		this.dialog.closeAll();
		this.submitSuccessEvent.emit("success");
	}

	public processSelectType(selectType: ApplicationWizardSelectType) {
		Object.assign(this.applicationState.application, selectType);

		if (selectType.selectType === "website") {
			this.buildWebUrl(this.applicationState.application);
		}
	}

	public processWebUrl(webUrl: string) {
		this.applicationState.webUrl = webUrl;
		this.tearDownWebUrl(webUrl);
		this.applicationState.webUrl = this.buildWebUrl(this.applicationState.application);
	}

	public processNetworkService(networkService: ApplicationWizardNetworkService) {
		Object.assign(this.applicationState.application, networkService);
	}

	public processPortsAndProtocols(portsProtocols: ApplicationWizardPortsProtocols) {
		this.applicationState.application.ports = portsProtocols.ports;
		this.applicationState.application.icmp = portsProtocols.icmp;
		if (this.applicationState.application.selectType === "website") {
			this.applicationState.webUrl = this.buildWebUrl(this.applicationState.application);
		}
	}

	public buildWebUrl(app: Resource): string {

		this.resetFirstPortProtocol();

		const prefix = app.urlPrefix || "http://";
		const port = getFirstPort(app, prefix);
		const suffix = app.urlSuffix || "";
		let splitter = "";
		if (suffix.length > 0 && !suffix.startsWith("/")) {
			splitter = "/";
		}
		return `${prefix}${app.ip}${port}${splitter}${suffix}`;
	}

	public tearDownWebUrl(url: string) {
		const parser = document.createElement("a");
		this.applicationState.application.urlPrefix = getPrefix(url);
		parser.href = getDefaultUrl(this.applicationState.application.urlPrefix, url);
		this.applicationState.application.ip = decodeURIComponent(parser.hostname);
		this.applicationState.application.ports[0] = getPort(this.applicationState.application.urlPrefix, parser.port, "tcp");
		this.applicationState.application.urlSuffix = getPathname(parser);
	}

	private resetFirstPortProtocol() {
		// if we've switched to a website, that first port must be the tcp port
		if (
			this.applicationState &&
			this.applicationState.application &&
			this.applicationState.application.selectType === "website"
		) {
			this.applicationState.application.ports[0].proto = "tcp";
		}
	}
}

function getFirstPort(app: Resource, prefix: string) {
	let port;
	const portZero = app.ports[0];
	if (isResourcePortSingle(portZero)) {
		port = portZero.port.toString();
	} else if (isResourcePortRange(portZero)) {
		throw new Error(`Port Range can not be used as first port for web apps`);
	}

	if (port !== "") {
		// clear the port if it's the same as the default for the protocol
		if (
			(prefix === "http://" && port === "80") ||
			(prefix === "https://" && port === "443")
		) {
			port = "";
		} else {
			port = `:${port}`;
		}
	}
	return port;
}

function getDefaultUrl(prefixIn: string | undefined, url: string): string {
	const prefix = prefixIn === undefined ? "http://" : prefixIn;
	if (url.startsWith(prefix)) {
		return url;
	} else {
		return `${prefix}${url}`;
	}
}

function getPathname(parser: HTMLAnchorElement): string {
	const search = parser.search;
	const pathname = parser.pathname === "/" ? "" : parser.pathname;
	return `${pathname}${search}`;
}

function getPrefix(url: string): any {
	const prefixIndex = url.indexOf("://");
	let prefix: string;
	if (prefixIndex > 0) {
		prefix = url.substring(0, prefixIndex + 3);
	} else {
		prefix = "http://";
	}
	return prefix;
}

function getPort(prefix: string | undefined, portIn: string, proto: Port.Proto): Resource.Port {
	let port = Number(portIn).valueOf();
	if (port === 0) {
		if (prefix !== undefined && prefix.startsWith("https")) {
			port = 443;
		} else {
			port = 80;
		}
	}
	return {
		version: 2,
		port: port,
		portNumber: port,
		proto: proto
	};
}

export function prettyPrint(port: Resource.Port | undefined) {
	let portString: string;
	if (!port) {
		portString = "";
	} else if (isResourcePortSingle(port)) {
		if (port.port === 0) {
			portString = "";
		} else {
			portString = port.port.toString();
		}
	} else if (isResourcePortRange(port)) {
		portString = `${port.portRange.min}-${port.portRange.max}`;
	} else {
		throw new Error(`unrecognized port: ${JSON.stringify(port)}`);
	}
	return portString;
}
